import { StudentClassificationStatus } from '../../types/studentClassification';
import type { OrchestratorRow } from '../../types/orchestrator';
import { hasRespondedFupLink, hasUnrespondedFupLink } from './fupLinkDisplay';

/** Same window as ORCHESTRATOR_ENTRY_CALENDAR_DAYS on the edge (adesão → copiar FUP). */
export const NEW_STUDENT_FUP_COPY_ELIGIBILITY_DAYS = 24;

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

function getBrazilTodayDateOnly(referenceDate: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(referenceDate);
}

function normalizeDateOnly(value: string | null | undefined): string | null {
  const match = value?.trim().match(DATE_ONLY_PATTERN);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));

  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
}

function addCalendarDaysDateOnly(dateOnly: string, days: number): string | null {
  const normalized = normalizeDateOnly(dateOnly);
  if (!normalized) {
    return null;
  }

  const [year, month, day] = normalized.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  parsed.setUTCDate(parsed.getUTCDate() + days);

  const nextYear = parsed.getUTCFullYear();
  const nextMonth = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const nextDay = String(parsed.getUTCDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

/** True when Brazil today is at least 24 calendar days after joined_at. */
export function hasReachedNewStudentFupCopyEligibility(
  joinedAt: string | null | undefined,
  today: string = getBrazilTodayDateOnly(),
): boolean {
  const joinedDate = normalizeDateOnly(joinedAt);
  if (!joinedDate) {
    return false;
  }

  const dueDate = addCalendarDaysDateOnly(joinedDate, NEW_STUDENT_FUP_COPY_ELIGIBILITY_DAYS);
  if (!dueDate) {
    return false;
  }

  return today >= dueDate;
}

/**
 * Aluna nova (não reativada) ainda em onboarding FUP: esconde copiar link.
 * A partir de 24 dias corridos da adesão (`joined_at`), deixa de pular o FUP.
 */
export function skipsFupAsNewStudent(
  row: OrchestratorRow,
  today: string = getBrazilTodayDateOnly(),
): boolean {
  if (row.is_reactivated) {
    return false;
  }

  const isNova = row.classification?.status === StudentClassificationStatus.NEW_STUDENT
    || row.is_new_student === true;

  if (!isNova) {
    return false;
  }

  if (hasReachedNewStudentFupCopyEligibility(row.joined_at, today)) {
    return false;
  }

  return true;
}

export function canViewFupResponse(
  row: OrchestratorRow,
): row is OrchestratorRow & { student_fup_id: number } {
  if (row.student_fup_id == null) {
    return false;
  }

  return row.has_fup_response || hasRespondedFupLink(row.fup_links ?? []);
}

function hasOrchestratorLastWorkoutDate(row: OrchestratorRow): boolean {
  const dateOnly = row.last_workout_date?.trim().slice(0, 10) ?? '';
  return dateOnly.length > 0;
}

/** Alinhado à edge: sem ciclo no período ou sem último treino, FUP liberado. */
export function isCycleFupSendAllowedForRow(row: OrchestratorRow): boolean {
  if (row.workout_assignment_id == null || !hasOrchestratorLastWorkoutDate(row)) {
    return true;
  }

  return row.cycle_fup_send_allowed;
}

/** Nova aluna sem link ainda: só "Ver resposta" (onboarding), sem copiar link. */
export function showFupOnboardingAnswersAction(
  row: OrchestratorRow,
  isNewStudent: boolean,
): boolean {
  return isNewStudent && !canViewFupResponse(row) && (row.fup_links ?? []).length === 0;
}

/** Copiar link + "Sem resposta" quando o ciclo permite e ainda há FUP sem resposta (inclui nova aluna com tentativa expirada). */
export function shouldShowFupCopyLinkActions(
  row: OrchestratorRow,
  isNewStudent: boolean,
): boolean {
  if (showFupOnboardingAnswersAction(row, isNewStudent)) {
    return false;
  }

  if (!isCycleFupSendAllowedForRow(row)) {
    return false;
  }

  if (canViewFupResponse(row)) {
    return false;
  }

  const fupLinks = row.fup_links ?? [];
  return !row.has_fup_response || hasUnrespondedFupLink(fupLinks);
}
